import React, { useEffect, useMemo, useState } from 'react';
import { regionMapping } from 'alakai-aloha-shared';
import { fetchRegionBySlug, generateRegionSlug } from '../../lib/sanityHelpers';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const TripThesisCard = ({ thesis, teasers, synthesisSnapshot, homebase }) => {
  const hasThesis = Boolean(thesis);
  const { islandConfig } = useMemo(() => getRuntimeSiteContext(), []);

  const normalizeName = useMemo(() => {
    return (value) => {
      if (!value) return value;
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[ʻ’']/g, '')
        .trim();
    };
  }, []);

  const regionName = useMemo(() => {
    const directRegion = synthesisSnapshot?.confirmedRegion
      || synthesisSnapshot?.homebaseSynthesis?.data?.region
      || synthesisSnapshot?.transformedData?.lodging?.bookedDetails?.region;

    if (directRegion) return directRegion;

    const areaCandidates = [
      synthesisSnapshot?.homebaseSynthesis?.data?.area,
      synthesisSnapshot?.transformedData?.lodging?.bookedDetails?.area,
    ].filter(Boolean);

    let inferredRegion = null;
    let inferredArea = areaCandidates[0] || null;

    if (homebase) {
      const parts = homebase
        .split('-')
        .map(part => part.trim())
        .filter(Boolean);

      if (parts.length > 0) {
        if (parts[1]) {
          inferredRegion = parts[1];
        }
        if (!inferredArea) {
          inferredArea = parts[0] || inferredArea;
        }
      }

      if (!inferredRegion) {
        const regionMatch = homebase.match(/\bin\s+([A-Za-zʻʼ'\-\s]+)/i);
        if (regionMatch && regionMatch[1]) {
          inferredRegion = regionMatch[1].trim().replace(/\s+area$/i, '');
        }
      }

      if (!inferredArea) {
        const areaMatch = homebase.match(/\(([^)]+?)\s+area\)/i);
        if (areaMatch && areaMatch[1]) {
          inferredArea = areaMatch[1].trim();
        }
      }
    }

    const areaFromCandidates = areaCandidates.find(Boolean) || inferredArea;
    if (!inferredRegion && areaFromCandidates) {
      inferredRegion = regionMapping[areaFromCandidates] || null;
      if (!inferredRegion) {
        const normalizedArea = normalizeName(areaFromCandidates);
        const matched = Object.entries(regionMapping).find(([key]) => normalizeName(key) === normalizedArea);
        if (matched) {
          inferredRegion = matched[1];
        }
      }
    }

    return inferredRegion || null;
  }, [
    synthesisSnapshot?.confirmedRegion,
    synthesisSnapshot?.homebaseSynthesis?.data?.region,
    synthesisSnapshot?.transformedData?.lodging?.bookedDetails?.region,
    synthesisSnapshot?.homebaseSynthesis?.data?.area,
    synthesisSnapshot?.transformedData?.lodging?.bookedDetails?.area,
    homebase,
    normalizeName
  ]);

  const regionSlug = useMemo(() => (regionName ? generateRegionSlug(regionName) : null), [regionName]);

  const [regionHero, setRegionHero] = useState(null);

  useEffect(() => {
    let active = true;

    if (!regionSlug) {
      setRegionHero(null);
      return () => {
        active = false;
      };
    }

    setRegionHero(prev => (prev?.slug === regionSlug ? prev : null));

    (async () => {
      try {
        const regionData = await fetchRegionBySlug(regionSlug);
        if (!active) return;
        const heroUrl = regionData?.heroImage?.asset?.url;
        if (heroUrl) {
          setRegionHero({
            slug: regionSlug,
            title: regionData?.title || regionName || null,
            alt: regionData?.heroImage?.alt || regionData?.title || regionName || 'Region landscape',
            imageUrl: heroUrl,
          });
        } else {
          setRegionHero(null);
        }
      } catch (err) {
        if (!active) return;
        console.warn('[TripThesisCard] Failed to fetch region hero:', err?.message || err);
        setRegionHero(null);
      }
    })();

    return () => {
      active = false;
    };
  }, [regionSlug, regionName]);

  const heroUrl = regionHero?.imageUrl || null;
  const heroAlt = regionHero?.alt || regionHero?.title || `${islandConfig.title} coastline`;

  if (!hasThesis) {
    return null;
  }

  const CardBody = ({ className = '' }) => (
    <div className={className}>
      <div className="mb-3">
        <h3 className="text-xl md:text-2xl font-bold text-white">Your Trip Summary</h3>
      </div>
      <p className="text-sm text-white leading-relaxed">{thesis}</p>
      {Array.isArray(teasers) && teasers.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
          {teasers.map((teaser, idx) => {
            const label = idx === 0
              ? 'Arrival'
              : idx === teasers.length - 1
                ? 'Departure'
                : `Day ${idx}`;
            return (
              <div key={idx} className="grid grid-cols-[9ch,1fr] text-sm text-white">
                <span className="font-medium text-white/90 whitespace-nowrap">{label}:</span>
                <span className="text-white">{teaser}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (heroUrl) {
    return (
      <div className="relative mb-6 rounded-lg overflow-hidden">
        <picture>
          <source srcSet={`${heroUrl}?auto=format&fit=crop&w=2000`} media="(min-width: 1024px)" />
          <source srcSet={`${heroUrl}?auto=format&fit=crop&w=1400`} media="(min-width: 640px)" />
          <img
            src={`${heroUrl}?auto=format&fit=crop&w=1200`}
            alt={heroAlt}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        <CardBody className="relative z-10 p-4 md:p-6" />
      </div>
    );
  }

  return (
    <div className="rounded-lg glass-card p-4 md:p-6 mb-6">
      <CardBody />
    </div>
  );
};

export default TripThesisCard;
